import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import { fetchConfig } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import MinecraftSkin from "@/components/MinecraftSkin";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { sfx } from "@/lib/sounds";
import { Trophy, Heart, Eye, Trash2, Link2, Unlink, User, ShieldCheck, Share2, Lock, Layers, Sparkles, Bell } from "lucide-react";
import NotificationPrefs from "@/components/NotificationPrefs";

const TABS = [
  { key: "overview",  label: "Overview", icon: User },
  { key: "edit",      label: "Edit Profile", icon: Sparkles },
  { key: "socials",   label: "Social Links", icon: Share2 },
  { key: "minecraft", label: "Minecraft", icon: Layers },
  { key: "privacy",   label: "Privacy", icon: Lock },
  { key: "notifs",    label: "Notifications", icon: Bell },
  { key: "builds",    label: "My Builds", icon: Trophy },
];

export default function Profile() {
  const { user, loading, updateProfile, updatePrivacy, linkMinecraft, unlinkMinecraft } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [mine, setMine] = useState([]);
  const [config, setConfig] = useState({ social_platforms: [], countries: [], ranks: [] });
  const [form, setForm] = useState({});
  const [social, setSocial] = useState({});
  const [privacy, setPrivacy] = useState({});
  const [mcInput, setMcInput] = useState("");

  useEffect(() => { if (!loading && !user) navigate("/"); }, [loading, user, navigate]);
  useEffect(() => { fetchConfig().then(setConfig).catch(() => {}); }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "", bio: user.bio || "", avatar: user.avatar || "",
      banner: user.banner || "", tagline: user.tagline || "",
      date_of_birth: user.date_of_birth || "", country: user.country || "",
      location: user.location || "", pronouns: user.pronouns || "",
      preferred_language: user.preferred_language || "English",
      favorite_server: user.favorite_server || "",
    });
    setSocial(user.social || {});
    setPrivacy(user.privacy || {});
    authClient.get("/builds?mine=true").then((r) => setMine(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const saveProfile = async () => {
    try { await updateProfile({ ...form, social }); sfx.success(); toast.success("Profile saved"); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const saveSocials = async () => {
    try { await updateProfile({ social }); sfx.success(); toast.success("Socials saved"); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const savePrivacy = async () => {
    try { await updatePrivacy(privacy); sfx.success(); toast.success("Privacy updated"); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const link = async () => {
    if (!mcInput.trim()) return;
    try { await linkMinecraft(mcInput.trim()); sfx.levelup(); toast.success(`Linked to ${mcInput}`); setMcInput(""); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const unlink = async () => {
    try { await unlinkMinecraft(); sfx.no(); toast.success("Minecraft account unlinked"); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const removeBuild = async (id) => {
    if (!window.confirm("Delete this build?")) return;
    try { await authClient.delete(`/builds/${id}`); setMine(mine.filter(b => b.id !== id)); sfx.no(); toast.success("Deleted"); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  const readImage = (file, cb, max = 1200) => {
    const fr = new FileReader();
    fr.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / img.width);
        const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h; c.getContext("2d").drawImage(img, 0, 0, w, h);
        cb(c.toDataURL("image/jpeg", 0.85));
      };
      img.src = fr.result;
    };
    fr.readAsDataURL(file);
  };

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-profile">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="rounded-2xl overflow-hidden border border-white/10 relative">
          <div className="h-40 sm:h-56 relative">
            {form.banner ? <img src={form.banner} alt="" className="w-full h-full object-cover" />
                         : <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/25 to-[#FF8C00]/10" />}
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
          </div>
          <div className="p-6 -mt-14 relative flex items-end gap-5 flex-wrap">
            <img src={user.avatar} alt="" className="w-24 h-24 rounded-2xl border-4 border-black object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">
                {user.rank || "Player"} · {user.provider}
              </div>
              <div className="font-hero text-4xl">{user.mc_username || user.name}</div>
              <div className="text-white/60 text-sm">{user.email}</div>
              {user.badges?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {user.badges.map((b) => (
                    <span key={b} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#FFBF00]/40 bg-[#FFBF00]/10 text-[#FFBF00]">
                      {b}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => navigate(`/users/${user.id}`)} className="px-4 py-2 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest">
              View public profile
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 my-6 border-b border-white/10 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => { setTab(key); sfx.click(); }}
                    data-testid={`profile-tab-${key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap ${
                      tab === key ? "border-[#FFBF00] text-[#FFBF00]" : "border-transparent text-white/60 hover:text-white"
                    }`}>
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="rounded-2xl glass p-5"><Trophy size={18} className="text-[#FFBF00] mb-3" />
              <div className="font-hero text-3xl">{user.achievements?.length || 0}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50">Achievements</div>
            </div>
            <div className="rounded-2xl glass p-5"><Heart size={18} className="text-[#FFBF00] mb-3" />
              <div className="font-hero text-3xl">{mine.reduce((s, b) => s + (b.likes_count || 0), 0)}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50">Total Likes</div>
            </div>
            <div className="rounded-2xl glass p-5"><Eye size={18} className="text-[#FFBF00] mb-3" />
              <div className="font-hero text-3xl">{mine.reduce((s, b) => s + (b.views || 0), 0)}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50">Total Views</div>
            </div>
            <div className="rounded-2xl glass p-5"><ShieldCheck size={18} className="text-[#FFBF00] mb-3" />
              <div className="font-hero text-3xl">{user.followers_count || 0}</div>
              <div className="text-[11px] uppercase tracking-widest text-white/50">Followers</div>
            </div>
          </div>
        )}

        {tab === "edit" && (
          <div className="grid md:grid-cols-2 gap-4" data-testid="profile-settings">
            <div className="rounded-2xl glass p-6 space-y-4">
              <Field label="Display Name">
                <Input data-testid="profile-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/60 border-white/10" />
              </Field>
              <Field label="Tagline">
                <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="e.g. Redstone wizard" className="bg-black/60 border-white/10" />
              </Field>
              <Field label="Bio">
                <Textarea value={form.bio || ""} rows={4} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-black/60 border-white/10" />
              </Field>
              <Field label="Avatar URL">
                <Input value={form.avatar || ""} onChange={(e) => setForm({ ...form, avatar: e.target.value })} className="bg-black/60 border-white/10" />
              </Field>
              <Field label="Banner Image">
                <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && readImage(e.target.files[0], (b64) => setForm({ ...form, banner: b64 }))}
                       className="text-xs text-white/60 file:mr-3 file:rounded-full file:border-0 file:bg-[#FFBF00]/15 file:text-[#FFBF00] file:px-3 file:py-1.5 file:text-[10px] file:uppercase file:tracking-widest" />
                {form.banner && <img src={form.banner} alt="" className="mt-2 rounded-md w-full h-24 object-cover" />}
              </Field>
            </div>
            <div className="rounded-2xl glass p-6 space-y-4">
              <Field label="Date of Birth"><Input type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="bg-black/60 border-white/10" /></Field>
              <Field label="Country">
                <select value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })}
                        className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm">
                  <option value="">Select country…</option>
                  {(config.countries || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="City / Location"><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Optional" className="bg-black/60 border-white/10" /></Field>
              <Field label="Pronouns"><Input value={form.pronouns || ""} onChange={(e) => setForm({ ...form, pronouns: e.target.value })} placeholder="e.g. she/her" className="bg-black/60 border-white/10" /></Field>
              <Field label="Preferred Language"><Input value={form.preferred_language || ""} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="bg-black/60 border-white/10" /></Field>
              <Field label="Favourite Server"><Input value={form.favorite_server || ""} onChange={(e) => setForm({ ...form, favorite_server: e.target.value })} placeholder="e.g. lifesteal" className="bg-black/60 border-white/10" /></Field>
              <button data-testid="profile-save" onClick={saveProfile} className="mt-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
                Save Profile
              </button>
            </div>
          </div>
        )}

        {tab === "socials" && (
          <div className="rounded-2xl glass p-6" data-testid="profile-socials">
            <div className="grid md:grid-cols-2 gap-3">
              {(config.social_platforms || []).filter(p => p.enabled).sort((a, b) => a.order - b.order).map((p) => (
                <label key={p.key} className="block">
                  <span className="text-[10px] uppercase tracking-widest text-white/50 flex items-center gap-1.5 mb-1">
                    <span style={{ color: p.color }}>●</span> {p.label}
                  </span>
                  <Input value={social[p.key] || ""} onChange={(e) => setSocial({ ...social, [p.key]: e.target.value })}
                         placeholder={p.placeholder} className="bg-black/60 border-white/10" />
                </label>
              ))}
            </div>
            <button onClick={saveSocials} className="mt-6 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
              Save Social Links
            </button>
          </div>
        )}

        {tab === "minecraft" && (
          <div className="rounded-2xl glass p-6 grid md:grid-cols-2 gap-6" data-testid="profile-mc">
            <div>
              {user.mc_username ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-[#FFBF00]">Linked Account</div>
                  <div className="font-hero text-3xl mt-1">{user.mc_username}</div>
                  <div className="text-[11px] font-mono text-white/50 mt-1">UUID · {user.mc_uuid}</div>
                  <button onClick={unlink} data-testid="profile-mc-unlink" className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:border-red-400/40 text-xs uppercase tracking-widest text-white/70">
                    <Unlink size={14} /> Unlink
                  </button>
                </>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Link Minecraft Username</div>
                  <div className="flex gap-2">
                    <Input data-testid="profile-mc-input" value={mcInput} onChange={(e) => setMcInput(e.target.value)} placeholder="e.g. Notch" className="bg-black/60 border-white/10" />
                    <button onClick={link} data-testid="profile-mc-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold whitespace-nowrap">
                      <Link2 size={14} /> Link
                    </button>
                  </div>
                  <div className="text-[11px] text-white/40 mt-2">We verify via Mojang — the username must exist.</div>
                </>
              )}
            </div>
            <div className="flex flex-col items-center">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-3">Preview</div>
              <MinecraftSkin username={user.mc_username || "MHF_Steve"} mode="body" size={180} cape />
              {user.mc_username && (
                <div className="mt-3 flex gap-2 text-[10px] uppercase tracking-widest">
                  <a href={`https://mc-heads.net/download/${user.mc_username}`} target="_blank" rel="noreferrer"
                     className="px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40">Skin file</a>
                  <a href={`https://namemc.com/profile/${user.mc_username}`} target="_blank" rel="noreferrer"
                     className="px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40">NameMC</a>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "privacy" && (
          <div className="rounded-2xl glass p-6 space-y-4 max-w-2xl" data-testid="profile-privacy">
            <Field label="Profile Visibility">
              <select value={privacy.profile_visibility || "public"} onChange={(e) => setPrivacy({ ...privacy, profile_visibility: e.target.value })}
                      className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm">
                <option value="public">Public — anyone can view</option>
                <option value="followers">Followers only</option>
                <option value="private">Private — only me</option>
              </select>
            </Field>
            {[
              ["show_email", "Show email on profile"],
              ["show_dob", "Show date of birth"],
              ["show_country", "Show country / location"],
              ["show_socials", "Show social links"],
              ["show_minecraft", "Show Minecraft account"],
              ["show_builds", "Show my builds"],
              ["show_activity", "Show my activity feed"],
              ["show_followers", "Show follower counts"],
              ["allow_follows", "Allow others to follow me"],
              ["require_follow_approval", "Require my approval before someone can follow"],
              ["allow_messages", "Allow direct messages"],
            ].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-white/5">
                <span className="text-sm text-white/80">{label}</span>
                <Switch checked={!!privacy[k]} onCheckedChange={(v) => setPrivacy({ ...privacy, [k]: v })} data-testid={`privacy-${k}`} />
              </div>
            ))}
            <button onClick={savePrivacy} data-testid="privacy-save" className="mt-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
              Save Privacy Settings
            </button>
          </div>
        )}

        {tab === "notifs" && <NotificationPrefs />}

        {tab === "builds" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="text-sm text-white/60">{mine.length} builds uploaded</div>
              <a href="/builds/upload" onClick={() => sfx.click()} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
                Upload New Build
              </a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {mine.map((b) => (
                <div key={b.id} className="rounded-xl overflow-hidden glass hover-lift">
                  <div className="aspect-video overflow-hidden relative">
                    {b.images?.[0] && <img src={b.images[0]} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-black/60 border border-white/10">
                      {b.approved ? <span className="text-green-400">Approved</span> : <span className="text-yellow-400">Pending Review</span>}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-hero text-xl">{b.title}</div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-white/60">
                      <span className="flex items-center gap-1"><Heart size={11} /> {b.likes_count}</span>
                      <span className="flex items-center gap-1"><Eye size={11} /> {b.views}</span>
                      <button onClick={() => removeBuild(b.id)} className="ml-auto text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {mine.length === 0 && (
              <div className="text-center text-white/50 py-16 rounded-xl glass">
                No builds yet. <a href="/builds/upload" className="text-[#FFBF00] hover:underline">Upload your first</a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      {children}
    </label>
  );
}
